import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

export interface AudioPlayerState {
	id: number | null;
	file: string | null;
	title: string | null;
	isPlaying: boolean;
}

// Imperative surface registered by the mounted MiniPlayer (which owns the
// actual <audio> element through @gfazioli/mantine-audio's useAudio).
export interface AudioPlayerHandle {
	play: () => void;
	pause: () => void;
	isPaused: () => boolean;
}

export interface AudioPlayerContextValue extends AudioPlayerState {
	// Same id while paused -> resume; new id -> swap the source and autoplay;
	// stop clears everything (which unmounts the MiniPlayer).
	play: (id: number, file: string, title: string) => void;
	pause: () => void;
	stop: () => void;
	setIsPlaying: (playing: boolean) => void;
	registerHandle: (handle: AudioPlayerHandle | null) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

const IDLE: AudioPlayerState = {
	id: null,
	file: null,
	title: null,
	isPlaying: false,
};

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<AudioPlayerState>(IDLE);
	const handleRef = useRef<AudioPlayerHandle | null>(null);
	const stateRef = useRef(state);
	stateRef.current = state;

	const registerHandle = useCallback((handle: AudioPlayerHandle | null) => {
		handleRef.current = handle;
	}, []);

	const play = useCallback((id: number, file: string, title: string) => {
		const current = stateRef.current;
		if (
			id === current.id &&
			handleRef.current &&
			handleRef.current.isPaused()
		) {
			handleRef.current.play();
			return;
		}
		if (id === current.id && current.isPlaying) {
			return;
		}
		// New track: the MiniPlayer remounts (key={id}) and autoplays.
		setState({ id, file, title, isPlaying: false });
	}, []);

	const pause = useCallback(() => {
		handleRef.current?.pause();
	}, []);

	const stop = useCallback(() => {
		handleRef.current?.pause();
		setState(IDLE);
	}, []);

	const setIsPlaying = useCallback((playing: boolean) => {
		setState((prev) =>
			prev.isPlaying === playing ? prev : { ...prev, isPlaying: playing }
		);
	}, []);

	const value = useMemo<AudioPlayerContextValue>(
		() => ({
			...state,
			play,
			pause,
			stop,
			setIsPlaying,
			registerHandle,
		}),
		[state, play, pause, stop, setIsPlaying, registerHandle]
	);

	return (
		<AudioPlayerContext.Provider value={value}>
			{children}
		</AudioPlayerContext.Provider>
	);
}

export function useAudioPlayer(): AudioPlayerContextValue {
	const ctx = useContext(AudioPlayerContext);
	if (!ctx) {
		throw new Error(
			"useAudioPlayer must be used within AudioPlayerProvider"
		);
	}
	return ctx;
}
